import { useEffect, type ReactNode } from 'react';
import { ResizableChatPanels } from './components/ResizableChatPanels';
import { useShallow } from 'zustand/react/shallow';
import { useAuth } from '@/entities/Auth/store';
import { useChatDialogWorkspace, useChatDialogWorkspaceApi } from '@/entities/ChatDialog/model/workspace';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { RequestStatus } from '@/shared/model/requestStatus';
import { RequestEmpty, RequestErrorBanner } from '@/shared/ui/RequestState';
import { ChatDialogList } from './components/ChatDialogList';
import { useChatDialogRealtimeState } from '@/features/chatDialogRealtime/model/useChatDialogRealtime';
import { useChatDialogRoute } from './useChatDialogRoute';
import { ChatDialogConversation } from './components/ChatDialogConversation';
import { SalesbotChatTasks } from '@/features/operatorTasks/ui/SalesbotChatTasks';
import classes from './ChatDialogPage.module.scss';

interface ChatDialogWorkspaceProps {
    /** Раздел переписки приложения */
    application?: boolean;
    historyOwnerAppUserId?: string;
    listContent?: ReactNode;

    /** Просмотр без действий от имени владельца */
    readOnly?: boolean;

    /** Действия выбранной переписки */
    headerActions?: ReactNode;
}

/** Показывает рабочее пространство переписки */
export const ChatDialogWorkspace = (
    { application = false, readOnly = false, headerActions, listContent, historyOwnerAppUserId }: ChatDialogWorkspaceProps
) => {
    const sessionRevision = useAuth((state) => state.sessionRevision);

    const canView = useCanAccessPermission(application
        ? 'admin.chats.view'
        : 'admin.chat-dialogs.view');

    const canRead = useCanAccessPermission(application
        ? 'admin.chats.read'
        : 'admin.chat-dialogs.read') && !readOnly;

    const canSend = useCanAccessPermission(application
        ? 'admin.chats.send'
        : 'admin.chat-dialogs.send') && !readOnly;

    const store = useChatDialogWorkspaceApi();
    const realtime = useChatDialogRealtimeState();
    const route = useChatDialogRoute(canView && realtime.connection !== 'connecting');

    const workspace = useChatDialogWorkspace(useShallow((state) => ({
        selectedDialogId: state.selectedDialog?.id,
        fetchDialogs: state.fetchDialogs,
        fetchCounters: state.fetchCounters,
        refreshLive: state.refreshLive,
        refreshStatus: state.refreshStatus,
        refreshError: state.refreshError,
        clearSelection: state.clearSelection,
    })));

    const { fetchDialogs, fetchCounters, refreshLive, clearSelection } = workspace;

    useEffect(() => {
        if (canView && realtime.connection === 'disabled') {
            const state = store.getState();

            if (state.listStatus === RequestStatus.Idle) void fetchDialogs();
            if (state.countersStatus === RequestStatus.Idle) void fetchCounters();
        }
    }, [canView, sessionRevision, realtime.connection, fetchCounters, fetchDialogs, store]);

    useEffect(() => clearSelection, [canView, sessionRevision, clearSelection]);

    if (!canView) return <RequestEmpty message="Нет доступа к переписке" />;

    return (
        <section
            className={classes.page}
            aria-label="Диалоги"
        >
            <RequestErrorBanner
                status={realtime.error
                    ? RequestStatus.Error
                    : RequestStatus.Success
                }
                error={realtime.error}
                fallbackMessage="Не удалось подключить обновления"
                onRetry={realtime.retry}
            />
            <RequestErrorBanner
                status={workspace.refreshStatus}
                error={workspace.refreshError}
                fallbackMessage="Не удалось обновить переписку"
                onRetry={refreshLive}
            />

            <ResizableChatPanels
                hasSelection={Boolean(workspace.selectedDialogId)}
                list={listContent ?? <ChatDialogList
                    onSelect={route.selectDialog}
                    onClose={route.closeDialog}
                />}
            >
                <ChatDialogConversation
                    key={workspace.selectedDialogId ?? 'empty'}
                    historyOwnerAppUserId={historyOwnerAppUserId}
                    headerActions={headerActions ?? (!application && !readOnly
                        ? <SalesbotChatTasks />
                        : undefined
                    )}
                    canRead={canRead}
                    canSend={canSend}
                    onClose={route.closeDialog}
                    shareUrl={route.dialogUrl}
                />
            </ResizableChatPanels>
        </section>
    );
};
